/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IActionContext } from '@msdyn365-commerce/core';
import {
    Address,
    AttributeValueBase,
    CardPaymentAcceptResult,
    CommerceProperty,
    GiftCard,
    SalesOrder,
    SimpleProduct,
    TenderLine,
    TokenizedPaymentCard
} from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';
import get from 'lodash/get';
import { action, computed, observable } from 'mobx';

import { GlobalState } from '../global-state/global-state';
import { ICartState } from '../state-interfaces/i-cart-state';
import {
    ICheckoutActionResult,
    ICheckoutGenericData,
    ICheckoutState,
    IGiftCardExtend,
    ICheckoutError
} from '../state-interfaces/i-checkout-state';
import { BaseCheckoutCartState } from './base-checkout-cart-state';
import { CheckoutStorageKey } from './checkout-state-storage';

/**
 * State information related to what is needed to succesfully perform a checkout.
 */
export class BaseCheckoutState extends GlobalState implements ICheckoutState {
    @observable protected _checkoutCart: ICartState;

    @computed public get checkoutCart(): ICartState {
        return this._checkoutCart;
    }

    @observable protected _salesOrder?: SalesOrder;

    @computed public get salesOrder(): Readonly<SalesOrder | undefined> {
        return this._salesOrder;
    }

    @observable protected _attributeValues: AttributeValueBase[];

    @computed public get attributeValues(): Readonly<AttributeValueBase[] | undefined> {
        return this._attributeValues;
    }

    @observable protected _extensionProperties: CommerceProperty[];

    @computed public get extensionProperties(): Readonly<CommerceProperty[] | undefined> {
        return this._extensionProperties;
    }

    @observable protected _orderedProducts?: SimpleProduct[];

    @computed public get orderedProducts(): Readonly<SimpleProduct[] | undefined> {
        return this._orderedProducts;
    }

    @observable protected _tokenizedPaymentCard?: TokenizedPaymentCard;

    @computed public get tokenizedPaymentCard(): Readonly<TokenizedPaymentCard | undefined> {
        return this._tokenizedPaymentCard;
    }

    @observable protected _tenderLine?: TenderLine;

    @computed public get tenderLine(): Readonly<TenderLine | undefined> {
        return this._tenderLine;
    }

    @observable protected _billingAddress?: Address;

    @computed public get billingAddress(): Readonly<Address | undefined> {
        return this._billingAddress;
    }

    @observable protected _isBillingAddressSameAsShippingAddress?: boolean;

    @computed public get isBillingAddressSameAsShippingAddress(): Readonly<boolean | undefined> {
        return this._isBillingAddressSameAsShippingAddress;
    }

    @observable protected _shippingAddress?: Address;

    @computed public get shippingAddress(): Readonly<Address | undefined> {
        return this._shippingAddress;
    }

    @observable protected _hasShippingAddress?: boolean;

    @computed public get hasShippingAddress(): Readonly<boolean | undefined> {
        return this._hasShippingAddress;
    }

    @observable protected _hasCartDeliveryMode?: boolean;

    @computed public get hasCartDeliveryMode(): Readonly<boolean | undefined> {
        return this._hasCartDeliveryMode;
    }

    @observable protected _cardPrefix?: string;

    @computed public get cardPrefix(): Readonly<string | undefined> {
        return this._cardPrefix;
    }

    @observable protected _paymentTenderType?: string;

    @computed public get paymentTenderType(): Readonly<string | undefined> {
        return this._paymentTenderType;
    }

    /**
     * @deprecated Use giftCardExtends method.
     */
    @computed public get giftCards(): Readonly<GiftCard[]> {
        return this.giftCardExtends;
    }

    @observable protected _giftCardExtends: IGiftCardExtend[] = [];

    @computed public get giftCardExtends(): Readonly<IGiftCardExtend[]> {
        return this._giftCardExtends;
    }

    @observable protected _loyaltyCardNumber?: string;

    @computed public get loyaltyCardNumber(): Readonly<string | undefined> {
        return this._loyaltyCardNumber;
    }

    @observable protected _loyaltyAmount: number = 0;

    @computed public get loyaltyAmount(): Readonly<number> {
        return this._loyaltyAmount;
    }

    @observable protected _guestCheckoutEmail: string = '';

    @computed public get guestCheckoutEmail(): Readonly<string> {
        return this._guestCheckoutEmail;
    }

    @observable protected _hasGuestCheckoutEmail?: boolean;

    @computed public get hasGuestCheckoutEmail(): Readonly<boolean | undefined> {
        return this._hasGuestCheckoutEmail;
    }

    @observable protected _isTermsAndConditionAccepted?: boolean;

    @computed public get isTermsAndConditionAccepted(): Readonly<boolean | undefined> {
        return this._isTermsAndConditionAccepted;
    }

    @observable protected _isTermsAndConditionChecked?: boolean;

    @computed public get isTermsAndConditionChecked(): Readonly<boolean | undefined> {
        return this._isTermsAndConditionChecked;
    }

    @observable protected _shouldEnableSinglePaymentAuthorizationCheckout?: boolean;

    @computed public get shouldEnableSinglePaymentAuthorizationCheckout(): Readonly<boolean | undefined> {
        return this._shouldEnableSinglePaymentAuthorizationCheckout;
    }

    @observable protected _shouldEnableCheckoutErrorDisplayMessaging?: boolean;

    @computed public get shouldEnableCheckoutErrorDisplayMessaging(): Readonly<boolean | undefined> {
        return this._shouldEnableCheckoutErrorDisplayMessaging;
    }

    @observable protected _shouldCollapsePaymentSection?: boolean;

    @computed public get shouldCollapsePaymentSection(): Readonly<boolean | undefined> {
        return this._shouldCollapsePaymentSection;
    }

    @observable protected _shouldTriggerPaymentWithPlaceOrder?: boolean;

    @computed public get shouldTriggerPaymentWithPlaceOrder(): Readonly<boolean | undefined> {
        return this._shouldTriggerPaymentWithPlaceOrder;
    }

    @observable protected _isPaymentSectionContainerReady?: boolean;

    @computed public get isPaymentSectionContainerReady(): Readonly<boolean | undefined> {
        return this._isPaymentSectionContainerReady;
    }

    @observable protected _cardPaymentAcceptResult?: CardPaymentAcceptResult;

    @computed public get cardPaymentAcceptResult(): Readonly<CardPaymentAcceptResult | undefined> {
        return this._cardPaymentAcceptResult;
    }

    @observable protected _isPaymentSectionContainerHasError?: boolean;

    @computed public get isPaymentSectionContainerHasError(): Readonly<boolean | undefined> {
        return this._isPaymentSectionContainerHasError;
    }

    @observable protected _redirectAdditionalContext?: string;

    @computed public get redirectAdditionalContext(): Readonly<string | undefined> {
        return this._redirectAdditionalContext;
    }

    @observable protected _redirectPaymentConnectorId?: string;

    @computed public get redirectPaymentConnectorId(): Readonly<string | undefined> {
        return this._redirectPaymentConnectorId;
    }

    @observable protected _checkoutErrorPaymentConnectorId?: string;

    @computed public get checkoutErrorPaymentConnectorId(): Readonly<string | undefined> {
        return this._checkoutErrorPaymentConnectorId;
    }

    @observable protected _isPlaceOrderEnabledInRedirection?: boolean;

    @computed public get isPlaceOrderEnabledInRedirection(): Readonly<boolean | undefined> {
        return this._isPlaceOrderEnabledInRedirection;
    }

    @observable protected _isPaymentVerificationRequired?: boolean;

    @computed public get isPaymentVerificationRequired(): Readonly<boolean | undefined> {
        return this._isPaymentVerificationRequired;
    }

    @observable protected _isCheckoutCallFailed?: boolean;

    @computed public get isCheckoutCallFailed(): Readonly<boolean | undefined> {
        return this._isCheckoutCallFailed;
    }

    @observable protected _customerAccountAmount: number = 0;

    @computed public get customerAccountAmount(): Readonly<number> {
        return this._customerAccountAmount;
    }

    @observable protected _shouldEnableExpressCheckoutInCheckout?: boolean;

    @computed public get shouldEnableExpressCheckoutInCheckout(): Readonly<boolean | undefined> {
        return this._shouldEnableExpressCheckoutInCheckout;
    }

    @observable protected _shouldEnableExpressCheckoutInCart?: boolean;

    @computed public get shouldEnableExpressCheckoutInCart(): Readonly<boolean | undefined> {
        return this._shouldEnableExpressCheckoutInCart;
    }

    @observable protected _shippingAddressFromExpressCheckout?: Address;

    @computed public get shippingAddressFromExpressCheckout(): Readonly<Address | undefined> {
        return this._shippingAddressFromExpressCheckout;
    }

    @observable protected _emailAddressFromExpressCheckout: string = '';

    @computed public get emailAddressFromExpressCheckout(): Readonly<string> {
        return this._emailAddressFromExpressCheckout;
    }

    @observable protected _billingAddressFromExpressCheckout?: Address;

    @computed public get billingAddressFromExpressCheckout(): Readonly<Address | undefined> {
        return this._billingAddressFromExpressCheckout;
    }

    @observable protected _isExpressCheckoutApplied?: boolean;

    @computed public get isExpressCheckoutApplied(): Readonly<boolean | undefined> {
        return this._isExpressCheckoutApplied;
    }

    @observable protected _isExpressAddressApplied?: boolean;

    @computed public get isExpressAddressApplied(): Readonly<boolean | undefined> {
        return this._isExpressAddressApplied;
    }

    @observable protected _isExpressCheckoutAppliedInCartPage?: boolean;

    @computed public get isExpressCheckoutAppliedInCartPage(): Readonly<boolean | undefined> {
        return this._isExpressCheckoutAppliedInCartPage;
    }

    @observable protected _isExpressPaymentModulesReady?: CommerceProperty[];

    @computed public get isExpressPaymentModulesReady(): Readonly<CommerceProperty[] | undefined> {
        return this._isExpressPaymentModulesReady;
    }

    @computed public get isPaymentVerificationRedirection(): Readonly<boolean> {
        const { requestFormData, query } = this.actionContext.requestContext;
        return !!(query && query.pv === '1' && (requestFormData || query.redirectResult));
    }

    @observable protected _checkoutError?: ICheckoutError;

    @computed public get checkoutError(): Readonly<ICheckoutError | undefined> {
        return this._checkoutError;
    }

    @observable protected _isBillingAddressHasError?: boolean;

    @computed public get isBillingAddressHasError(): Readonly<boolean | undefined> {
        return this._isBillingAddressHasError;
    }

    @observable protected _checkoutGenericData: object = {};

    constructor(actionContext: IActionContext) {
        super(actionContext);
        this._checkoutCart = new BaseCheckoutCartState(actionContext);
        this._attributeValues = [];
        this._extensionProperties = [];

        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- Do not need type check for app settings.
        this._shouldEnableSinglePaymentAuthorizationCheckout = actionContext.requestContext.app.config
            .shouldEnableSinglePaymentAuthorizationCheckout as boolean;

        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- Do not need type check for app settings.
        this._shouldEnableCheckoutErrorDisplayMessaging = actionContext.requestContext.app.config
            .shouldEnableCheckoutErrorDisplayMessaging as boolean;
    }

    @action
    public updateSalesOrder(input: {
        newSalesOrder: SalesOrder;
        newOrderedProducts: SimpleProduct[];
        additionalProperties?: object;
    }): Promise<ICheckoutActionResult> {
        this._salesOrder = input.newSalesOrder;
        this._orderedProducts = input.newOrderedProducts;
        this._attributeValues = input.newSalesOrder.AttributeValues || [];
        this._extensionProperties = input.newSalesOrder.ExtensionProperties || [];

        return Promise.resolve(<ICheckoutActionResult>{ status: 'SUCCESS' });
    }

    @action
    public async updateAttributeValues(input: {
        newAttributeValues: AttributeValueBase[];
        additionalProperties?: object;
    }): Promise<ICheckoutActionResult> {
        this._attributeValues = input.newAttributeValues;

        return Promise.resolve(<ICheckoutActionResult>{ status: 'SUCCESS' });
    }

    @action
    public async updateExtensionProperties(input: {
        newExtensionProperties: CommerceProperty[];
        additionalProperties?: object;
    }): Promise<ICheckoutActionResult> {
        this._extensionProperties = input.newExtensionProperties;

        return Promise.resolve(<ICheckoutActionResult>{ status: 'SUCCESS' });
    }

    @action
    public updateTokenizedPaymentCard(input: {
        newTokenizedPaymentCard?: TokenizedPaymentCard;
        additionalProperties?: object;
    }): Promise<ICheckoutActionResult> {
        this._tokenizedPaymentCard = input.newTokenizedPaymentCard;

        return Promise.resolve(<ICheckoutActionResult>{ status: 'SUCCESS' });
    }

    @action
    public updateTenderLine(input: { newTenderLine?: TenderLine; additionalProperties?: object }): Promise<ICheckoutActionResult> {
        this._tenderLine = input.newTenderLine;

        return Promise.resolve(<ICheckoutActionResult>{ status: 'SUCCESS' });
    }

    @action
    public async updateShippingAddress(input: {
        newShippingAddress: Address;
        additionalProperties?: object;
    }): Promise<ICheckoutActionResult> {
        this._shippingAddress = input.newShippingAddress;

        return Promise.resolve(<ICheckoutActionResult>{ status: 'SUCCESS' });
    }

    @action
    public async updateHasShippingAddress(
        // eslint-disable-next-line @typescript-eslint/naming-convention -- Keep the same naming pattern.
        input: { newHasShippingAddress: boolean; additionalProperties?: { [key: string]: unknown } }
    ): Promise<ICheckoutActionResult> {
        this._hasShippingAddress = input.newHasShippingAddress;

        const result: ICheckoutActionResult = { status: 'SUCCESS' };
        return Promise.resolve(result);
    }

    @action
    public async updateHasCartDeliveryMode(
        // eslint-disable-next-line @typescript-eslint/naming-convention -- Keep the same naming pattern.
        input: { newHasCartDeliveryMode: boolean; additionalProperties?: { [key: string]: unknown } }
    ): Promise<ICheckoutActionResult> {
        this._hasCartDeliveryMode = input.newHasCartDeliveryMode;

        const result: ICheckoutActionResult = { status: 'SUCCESS' };
        return Promise.resolve(result);
    }

    @action
    public async updateTermsAndConditionsAcceptance(input: {
        newIsTermsAndConditionAccepted: boolean;
        additionalProperties?: object;
    }): Promise<ICheckoutActionResult> {
        this._isTermsAndConditionAccepted = input.newIsTermsAndConditionAccepted;

        return Promise.resolve(<ICheckoutActionResult>{ status: 'SUCCESS' });
    }

    @action
    public async updateIsTermsAndConditionsChecked(
        // eslint-disable-next-line @typescript-eslint/naming-convention -- Keep the same naming pattern.
        input: { newIsTermsAndConditionChecked: boolean; additionalProperties?: { [key: string]: unknown } }
    ): Promise<ICheckoutActionResult> {
        this._isTermsAndConditionChecked = input.newIsTermsAndConditionChecked;

        const result: ICheckoutActionResult = { status: 'SUCCESS' };
        return Promise.resolve(result);
    }

    @action
    public async updateShouldCollapsePaymentSection(
        // eslint-disable-next-line @typescript-eslint/naming-convention -- Keep the same naming pattern.
        input: { newShouldCollapsePaymentSection: boolean; additionalProperties?: { [key: string]: unknown } }
    ): Promise<ICheckoutActionResult> {
        this._shouldCollapsePaymentSection = input.newShouldCollapsePaymentSection;

        const result: ICheckoutActionResult = { status: 'SUCCESS' };
        return Promise.resolve(result);
    }

    @action
    public async updateShouldTriggerPaymentWithPlaceOrder(
        // eslint-disable-next-line @typescript-eslint/naming-convention -- Keep the same naming pattern.
        input: { newShouldTriggerPaymentWithPlaceOrder: boolean; additionalProperties?: { [key: string]: unknown } }
    ): Promise<ICheckoutActionResult> {
        this._shouldTriggerPaymentWithPlaceOrder = input.newShouldTriggerPaymentWithPlaceOrder;

        const result: ICheckoutActionResult = { status: 'SUCCESS' };
        return Promise.resolve(result);
    }

    @action
    public async updateIsPaymentSectionContainerReady(
        // eslint-disable-next-line @typescript-eslint/naming-convention -- Keep the same naming pattern.
        input: { newIsPaymentSectionContainerReady: boolean; additionalProperties?: { [key: string]: unknown } }
    ): Promise<ICheckoutActionResult> {
        this._isPaymentSectionContainerReady = input.newIsPaymentSectionContainerReady;

        const result: ICheckoutActionResult = { status: 'SUCCESS' };
        return Promise.resolve(result);
    }

    @action
    public async updateCardPaymentAcceptResult(input: {
        newCardPaymentAcceptResult: CardPaymentAcceptResult;
        additionalProperties?: { [key: string]: unknown };
    }): Promise<ICheckoutActionResult> {
        this._cardPaymentAcceptResult = input.newCardPaymentAcceptResult;

        const result: ICheckoutActionResult = { status: 'SUCCESS' };
        return Promise.resolve(result);
    }

    @action
    public async updateIsPaymentSectionContainerHasError(
        // eslint-disable-next-line @typescript-eslint/naming-convention -- Keep the same naming pattern.
        input: { newIsPaymentSectionContainerHasError: boolean; additionalProperties?: { [key: string]: unknown } }
    ): Promise<ICheckoutActionResult> {
        this._isPaymentSectionContainerHasError = input.newIsPaymentSectionContainerHasError;

        const result: ICheckoutActionResult = { status: 'SUCCESS' };
        return Promise.resolve(result);
    }

    @action
    public async updateRedirectAdditionalContext(input: {
        newRedirectAdditionalContext: string;
        additionalProperties?: { [key: string]: unknown };
    }): Promise<ICheckoutActionResult> {
        this._redirectAdditionalContext = input.newRedirectAdditionalContext;

        const result: ICheckoutActionResult = { status: 'SUCCESS' };
        return Promise.resolve(result);
    }

    @action
    public async updateRedirectPaymentConnectorId(input: {
        newRedirectPaymentConnectorId: string;
        additionalProperties?: { [key: string]: unknown };
    }): Promise<ICheckoutActionResult> {
        this._redirectPaymentConnectorId = input.newRedirectPaymentConnectorId;

        const result: ICheckoutActionResult = { status: 'SUCCESS' };
        return Promise.resolve(result);
    }

    @action
    public async updateCheckoutErrorPaymentConnectorId(input: {
        newCheckoutErrorPaymentConnectorId: string;
        additionalProperties?: { [key: string]: unknown };
    }): Promise<ICheckoutActionResult> {
        this._checkoutErrorPaymentConnectorId = input.newCheckoutErrorPaymentConnectorId;

        const result: ICheckoutActionResult = { status: 'SUCCESS' };
        return Promise.resolve(result);
    }

    @action
    public async updateIsCheckoutCallFailed(
        // eslint-disable-next-line @typescript-eslint/naming-convention -- Keep the same naming pattern.
        input: { newIsCheckoutCallFailed: boolean; additionalProperties?: { [key: string]: unknown } }
    ): Promise<ICheckoutActionResult> {
        this._isCheckoutCallFailed = input.newIsCheckoutCallFailed;

        const result: ICheckoutActionResult = { status: 'SUCCESS' };
        return Promise.resolve(result);
    }

    @action
    public async updateIsPlaceOrderEnabledInRedirection(
        // eslint-disable-next-line @typescript-eslint/naming-convention -- Keep the same naming pattern.
        input: { newIsPlaceOrderEnabledInRedirection: boolean; additionalProperties?: { [key: string]: unknown } }
    ): Promise<ICheckoutActionResult> {
        this._isPlaceOrderEnabledInRedirection = input.newIsPlaceOrderEnabledInRedirection;

        const result: ICheckoutActionResult = { status: 'SUCCESS' };
        return Promise.resolve(result);
    }

    @action
    public async updateIsPaymentVerificationRequired(
        // eslint-disable-next-line @typescript-eslint/naming-convention -- Keep the same naming pattern.
        input: { newIsPaymentVerificationRequired: boolean; additionalProperties?: { [key: string]: unknown } }
    ): Promise<ICheckoutActionResult> {
        this._isPaymentVerificationRequired = input.newIsPaymentVerificationRequired;

        const result: ICheckoutActionResult = { status: 'SUCCESS' };
        return Promise.resolve(result);
    }

    @action
    public updateBillingAddress(input: { newBillingAddress: Address; additionalProperties?: object }): Promise<ICheckoutActionResult> {
        this._billingAddress = input.newBillingAddress;

        // @ts-expect-error
        this._isBillingAddressSameAsShippingAddress = input.additionalProperties?.isBillingAddressSameAsShippingAddress || false;

        return Promise.resolve(<ICheckoutActionResult>{ status: 'SUCCESS' });
    }

    @action
    public removeBillingAddress(input: { additionalProperties?: object }): Promise<ICheckoutActionResult> {
        this._billingAddress = undefined;
        this._isBillingAddressSameAsShippingAddress = undefined;

        return Promise.resolve(<ICheckoutActionResult>{ status: 'SUCCESS' });
    }

    @action
    public updatePaymentTenderType(input: {
        newPaymentTenderType?: string;
        additionalProperties?: object;
    }): Promise<ICheckoutActionResult> {
        this._paymentTenderType = input.newPaymentTenderType;

        return Promise.resolve(<ICheckoutActionResult>{ status: 'SUCCESS' });
    }

    @action
    public updateCardPrefix(input: { newCardPrefix: string; additionalProperties?: object }): Promise<ICheckoutActionResult> {
        this._cardPrefix = input.newCardPrefix;

        return Promise.resolve(<ICheckoutActionResult>{ status: 'SUCCESS' });
    }

    @action
    public removeGiftCard(input: { giftCardNumber: string; additionalProperties?: object }): Promise<ICheckoutActionResult> {
        this._giftCardExtends = this._giftCardExtends.filter(giftCard => giftCard.Id !== input.giftCardNumber);

        return Promise.resolve(<ICheckoutActionResult>{ status: 'SUCCESS' });
    }

    @action
    public removeGiftCardExtend(input: { giftCardNumber: string; additionalProperties?: object }): Promise<ICheckoutActionResult> {
        this._giftCardExtends = this._giftCardExtends.filter(giftCard => giftCard.Id !== input.giftCardNumber);

        return Promise.resolve(<ICheckoutActionResult>{ status: 'SUCCESS' });
    }

    @action
    public addGiftCard(input: { giftCard: GiftCard; additionalProperties?: object }): Promise<ICheckoutActionResult> {
        // @ts-expect-error
        const { Pin, ExpirationDate, TenderTypeId } = input.additionalProperties || {};

        const gitCardExtend = { ...input.giftCard, ...{ Pin, ExpirationDate, TenderTypeId } };

        this._giftCardExtends = [gitCardExtend, ...this._giftCardExtends];

        return Promise.resolve(<ICheckoutActionResult>{ status: 'SUCCESS' });
    }

    @action
    public addGiftCardExtend(input: { giftCard: IGiftCardExtend; additionalProperties?: object }): Promise<ICheckoutActionResult> {
        this._giftCardExtends = [input.giftCard, ...this._giftCardExtends];

        return Promise.resolve(<ICheckoutActionResult>{ status: 'SUCCESS' });
    }

    @action
    public updateLoyaltyCardNumber(input: { newLoyaltyCardNumber: string; additionalProperties?: object }): Promise<ICheckoutActionResult> {
        this._loyaltyCardNumber = input.newLoyaltyCardNumber;

        return Promise.resolve(<ICheckoutActionResult>{ status: 'SUCCESS' });
    }

    @action
    public updateLoyaltyAmount(input: { newAmount: number; additionalProperties?: object }): Promise<ICheckoutActionResult> {
        this._loyaltyAmount = Number(input.newAmount.toFixed(2));

        return Promise.resolve(<ICheckoutActionResult>{ status: 'SUCCESS' });
    }

    @action
    public async updateGuestCheckoutEmail(input: {
        newGuestCheckoutEmail: string;
        additionalProperties?: object;
    }): Promise<ICheckoutActionResult> {
        this._guestCheckoutEmail = input.newGuestCheckoutEmail;
        const emailOnCart = this.checkoutCart.cart.ReceiptEmail;

        if (emailOnCart !== input.newGuestCheckoutEmail) {
            await this.checkoutCart.updateReceiptEmail({ newEmail: input.newGuestCheckoutEmail });
        }

        return Promise.resolve(<ICheckoutActionResult>{ status: 'SUCCESS' });
    }

    @action
    public async updateHasGuestCheckoutEmail(
        // eslint-disable-next-line @typescript-eslint/naming-convention -- Keep the same naming pattern.
        input: { newHasGuestCheckoutEmail: boolean; additionalProperties?: { [key: string]: unknown } }
    ): Promise<ICheckoutActionResult> {
        this._hasGuestCheckoutEmail = input.newHasGuestCheckoutEmail;

        const result: ICheckoutActionResult = { status: 'SUCCESS' };
        return Promise.resolve(result);
    }

    @action
    public removeCheckoutCartId(input: { additionalProperties?: object }): Promise<ICheckoutActionResult> {
        this.actionContext.requestContext.cookies.removeCheckoutCartCookie();
        return Promise.resolve(<ICheckoutActionResult>{ status: 'SUCCESS' });
    }

    @action
    public saveDataInStorage(input: { additionalProperties?: object }): Promise<ICheckoutActionResult> {
        const { sessionStorage } = this.actionContext.requestContext;

        // Add all local data to session storage
        if (this.billingAddress) {
            sessionStorage.setCheckoutCartData(CheckoutStorageKey.BillingAddress, {
                billingAddress: this.billingAddress,
                isBillingAddressSameAsShippingAddress: this.isBillingAddressSameAsShippingAddress
            });
        }

        if (this.giftCardExtends && this.giftCardExtends.length > 0) {
            sessionStorage.setCheckoutCartData(CheckoutStorageKey.GiftCard, this.giftCardExtends);
        }

        if (this.loyaltyCardNumber) {
            sessionStorage.setCheckoutCartData(CheckoutStorageKey.LoyaltyCardNumber, { loyaltyCardNumber: this.loyaltyCardNumber });
        }

        if (this.loyaltyAmount) {
            sessionStorage.setCheckoutCartData(CheckoutStorageKey.LoyaltyAmount, { loyaltyAmount: this.loyaltyAmount });
        }

        if (this.isTermsAndConditionAccepted) {
            sessionStorage.setCheckoutCartData(CheckoutStorageKey.TermsAndCondition, {
                isTermsAndConditionAccepted: this.isTermsAndConditionAccepted
            });
        }

        if (this.paymentTenderType) {
            window.sessionStorage.setItem(CheckoutStorageKey.PaymentTenderType, this.paymentTenderType);
        }

        if (this.redirectPaymentConnectorId) {
            window.sessionStorage.setItem(CheckoutStorageKey.RedirectPaymentConnectorId, this.redirectPaymentConnectorId);
        }

        if (this.isExpressCheckoutApplied) {
            window.sessionStorage.setItem(CheckoutStorageKey.IsExpressCheckoutApplied, this.isExpressCheckoutApplied.toString());
        }

        // Save generic data with SaveDataBeforeRedirect value true
        const filtered = Object.keys(this._checkoutGenericData).reduce((filteredData: object, key: string) => {
            if (this._checkoutGenericData[key].SaveDataBeforeRedirect) {
                filteredData[key] = this._checkoutGenericData[key];
            }
            return filteredData;
        }, {});

        if (Object.keys(filtered).length > 0) {
            sessionStorage.setCheckoutCartData(CheckoutStorageKey.CheckoutGenericData, { checkoutGenericData: filtered });
        }

        return Promise.resolve(<ICheckoutActionResult>{ status: 'SUCCESS' });
    }

    @action
    public updateCustomerAccountAmount(input: { newAmount: number; additionalProperties?: object }): Promise<ICheckoutActionResult> {
        this._customerAccountAmount = Number(input.newAmount.toFixed(2));

        return Promise.resolve(<ICheckoutActionResult>{ status: 'SUCCESS' });
    }

    @action
    public async updateBillingAddressFromExpressCheckout(input: {
        newBillingAddressFromExpressCheckout: Address;
        additionalProperties?: { [key: string]: unknown };
    }): Promise<ICheckoutActionResult> {
        this._billingAddressFromExpressCheckout = input.newBillingAddressFromExpressCheckout;

        const result: ICheckoutActionResult = { status: 'SUCCESS' };
        return Promise.resolve(result);
    }

    @action
    public async updateShippingAddressFromExpressCheckout(input: {
        newShippingAddressFromExpressCheckout: Address;
        additionalProperties?: { [key: string]: unknown };
    }): Promise<ICheckoutActionResult> {
        this._shippingAddressFromExpressCheckout = input.newShippingAddressFromExpressCheckout;

        const result: ICheckoutActionResult = { status: 'SUCCESS' };
        return Promise.resolve(result);
    }

    @action
    public async updateEmailAddressFromExpressCheckout(input: {
        newEmailAddressFromExpressCheckout: string;
        additionalProperties?: { [key: string]: unknown };
    }): Promise<ICheckoutActionResult> {
        this._emailAddressFromExpressCheckout = input.newEmailAddressFromExpressCheckout;

        const result: ICheckoutActionResult = { status: 'SUCCESS' };
        return Promise.resolve(result);
    }

    @action
    public async updateShouldEnableExpressCheckoutInCheckout(
        // eslint-disable-next-line @typescript-eslint/naming-convention -- Keep the same naming pattern.
        input: { newShouldEnableExpressCheckoutInCheckout: boolean; additionalProperties?: { [key: string]: unknown } }
    ): Promise<ICheckoutActionResult> {
        this._shouldEnableExpressCheckoutInCheckout = input.newShouldEnableExpressCheckoutInCheckout;

        const result: ICheckoutActionResult = { status: 'SUCCESS' };
        return Promise.resolve(result);
    }

    @action
    public async updateShouldEnableExpressCheckoutInCart(
        // eslint-disable-next-line @typescript-eslint/naming-convention -- Keep the same naming pattern.
        input: { newShouldEnableExpressCheckoutInCart: boolean; additionalProperties?: { [key: string]: unknown } }
    ): Promise<ICheckoutActionResult> {
        this._shouldEnableExpressCheckoutInCart = input.newShouldEnableExpressCheckoutInCart;

        const result: ICheckoutActionResult = { status: 'SUCCESS' };
        return Promise.resolve(result);
    }

    @action
    public async updateIsExpressCheckoutApplied(
        // eslint-disable-next-line @typescript-eslint/naming-convention -- Keep the same naming pattern.
        input: { newIsExpressCheckoutApplied: boolean; additionalProperties?: { [key: string]: unknown } }
    ): Promise<ICheckoutActionResult> {
        this._isExpressCheckoutApplied = input.newIsExpressCheckoutApplied;

        const result: ICheckoutActionResult = { status: 'SUCCESS' };
        return Promise.resolve(result);
    }

    @action
    public async updateIsExpressAddressApplied(
        // eslint-disable-next-line @typescript-eslint/naming-convention -- Keep the same naming pattern.
        input: { newIsExpressAddressApplied: boolean; additionalProperties?: { [key: string]: unknown } }
    ): Promise<ICheckoutActionResult> {
        this._isExpressAddressApplied = input.newIsExpressAddressApplied;

        const result: ICheckoutActionResult = { status: 'SUCCESS' };
        return Promise.resolve(result);
    }

    @action
    public async updateIsExpressCheckoutAppliedInCartPage(
        // eslint-disable-next-line @typescript-eslint/naming-convention -- Keep the same naming pattern.
        input: { newIsExpressCheckoutAppliedInCartPage: boolean; additionalProperties?: { [key: string]: unknown } }
    ): Promise<ICheckoutActionResult> {
        this._isExpressCheckoutAppliedInCartPage = input.newIsExpressCheckoutAppliedInCartPage;

        const result: ICheckoutActionResult = { status: 'SUCCESS' };
        return Promise.resolve(result);
    }

    @action
    public async updateIsBillingAddressHasError(
        // eslint-disable-next-line @typescript-eslint/naming-convention -- Keep the same naming pattern.
        input: { newIsBillingAddressHasError: boolean; additionalProperties?: { [key: string]: unknown } }
    ): Promise<ICheckoutActionResult> {
        this._isBillingAddressHasError = input.newIsBillingAddressHasError;

        const result: ICheckoutActionResult = { status: 'SUCCESS' };
        return Promise.resolve(result);
    }

    @action
    public async updateIsExpressPaymentModulesReady(
        // eslint-disable-next-line @typescript-eslint/naming-convention -- Keep the same naming pattern.
        input: {
            newIsExpressPaymentModulesReady: CommerceProperty[];
            additionalProperties?: { [key: string]: unknown };
        }
    ): Promise<ICheckoutActionResult> {
        this._isExpressPaymentModulesReady = input.newIsExpressPaymentModulesReady;

        const result: ICheckoutActionResult = { status: 'SUCCESS' };
        return Promise.resolve(result);
    }

    @action
    public async updateExpressCheckoutDetails(input: {
        // eslint-disable-next-line @typescript-eslint/naming-convention -- Keep the same naming pattern.
        newIsExpressCheckoutApplied: boolean;
        // eslint-disable-next-line @typescript-eslint/naming-convention -- Keep the same naming pattern.
        newIsExpressAddressApplied?: boolean;
        newCardPaymentAcceptResult?: CardPaymentAcceptResult;
        newEmailAddressFromExpressCheckout: string;
        newTenderLine?: TenderLine;
        newPaymentTenderType?: string;
        newTokenizedPaymentCard?: TokenizedPaymentCard;
        additionalProperties?: { [key: string]: unknown };
    }): Promise<ICheckoutActionResult> {
        this._isExpressCheckoutApplied = input.newIsExpressCheckoutApplied;

        if (input.newIsExpressAddressApplied !== undefined) {
            this._isExpressAddressApplied = input.newIsExpressAddressApplied;
        }

        if (input.newCardPaymentAcceptResult !== undefined) {
            this._cardPaymentAcceptResult = input.newCardPaymentAcceptResult;
        }

        if (input.newPaymentTenderType !== undefined) {
            this._paymentTenderType = input.newPaymentTenderType;
        }

        if (input.newTokenizedPaymentCard !== undefined) {
            this._tokenizedPaymentCard = input.newTokenizedPaymentCard;
        }

        if (input.newTenderLine !== undefined) {
            this._tenderLine = input.newTenderLine;
        }

        if (input.newEmailAddressFromExpressCheckout) {
            this._emailAddressFromExpressCheckout = input.newEmailAddressFromExpressCheckout;
        }

        const result: ICheckoutActionResult = { status: 'SUCCESS' };
        return Promise.resolve(result);
    }

    @action
    public async updateCheckoutError(input: {
        newCheckoutError: ICheckoutError;
        additionalProperties?: { [key: string]: unknown };
    }): Promise<ICheckoutActionResult> {
        this._checkoutError = input.newCheckoutError;

        const result: ICheckoutActionResult = { status: 'SUCCESS' };
        return Promise.resolve(result);
    }

    @action
    public setCheckoutGenericData(input: {
        key: string;
        checkoutGenericData: ICheckoutGenericData;
        additionalProperties?: object;
    }): Promise<ICheckoutActionResult> {
        this._checkoutGenericData[input.key] = input.checkoutGenericData;
        return Promise.resolve(<ICheckoutActionResult>{ status: 'SUCCESS' });
    }

    @action
    public getCheckoutGenericData(input: { key: string }): ICheckoutGenericData | undefined {
        return this._checkoutGenericData[input.key];
    }

    public async initialize(): Promise<void> {
        if (this.isInitialized) {
            return;
        }

        await this._checkoutCart.initialize();

        this.setInitialData();
        this.isInitialized = true;
        this._status = 'READY';
        this._attributeValues = [];
        this._extensionProperties = [];
    }

    @action
    private cleanCheckoutData(input: { additionalProperties?: object }): Promise<ICheckoutActionResult> {
        this.actionContext.requestContext.sessionStorage.removeCheckoutCartData();
        return Promise.resolve(<ICheckoutActionResult>{ status: 'SUCCESS' });
    }

    private setInitialData() {
        const { sessionStorage } = this.actionContext.requestContext;

        if (this.isPaymentVerificationRedirection) {
            const billingAddress = sessionStorage.getCheckoutCartData(CheckoutStorageKey.BillingAddress);
            const giftCard = sessionStorage.getCheckoutCartData(CheckoutStorageKey.GiftCard);

            this._billingAddress = get(billingAddress, 'billingAddress');
            this._isBillingAddressSameAsShippingAddress = get(billingAddress, 'isBillingAddressSameAsShippingAddress');
            this._giftCardExtends = (giftCard && Array.isArray(giftCard) ? giftCard : []) as IGiftCardExtend[];
            this._loyaltyCardNumber = get(sessionStorage.getCheckoutCartData(CheckoutStorageKey.LoyaltyCardNumber), 'loyaltyCardNumber');
            this._loyaltyAmount = get(sessionStorage.getCheckoutCartData(CheckoutStorageKey.LoyaltyAmount), 'loyaltyAmount');
            this._isTermsAndConditionAccepted = get(
                sessionStorage.getCheckoutCartData(CheckoutStorageKey.TermsAndCondition),
                'isTermsAndConditionAccepted'
            );
            this._checkoutGenericData =
                get(sessionStorage.getCheckoutCartData(CheckoutStorageKey.CheckoutGenericData), 'checkoutGenericData') || {};
            this._paymentTenderType = window.sessionStorage.getItem(CheckoutStorageKey.PaymentTenderType) ?? undefined;
            this._redirectPaymentConnectorId = window.sessionStorage.getItem(CheckoutStorageKey.RedirectPaymentConnectorId) as string;
            this._isExpressCheckoutApplied = (window.sessionStorage.getItem(CheckoutStorageKey.IsExpressCheckoutApplied) as string)
                ? true
                : false;
        }

        this.cleanCheckoutData({});
    }
}
